/**
 * Class Settings React Query Hooks
 * Class Types, Subjects, Custom Fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { classSettingsService } from '../services';

const CLASS_TYPES_KEY = 'classTypes';
const SUBJECTS_KEY = 'subjects';
const CUSTOM_FIELDS_KEY = 'classCustomFields';

// Class Types
export function useClassTypes() {
  return useQuery({
    queryKey: [CLASS_TYPES_KEY],
    queryFn: () => classSettingsService.getClassTypes(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateClassType() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: classSettingsService.createClassType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLASS_TYPES_KEY] });
    },
  });
}

export function useUpdateClassType() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => classSettingsService.updateClassType(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLASS_TYPES_KEY] });
    },
  });
}

export function useDeleteClassType() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: classSettingsService.deleteClassType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CLASS_TYPES_KEY] });
    },
  });
}

// Subjects
export function useSubjects() {
  return useQuery({
    queryKey: [SUBJECTS_KEY],
    queryFn: () => classSettingsService.getSubjects(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateSubject() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: classSettingsService.createSubject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SUBJECTS_KEY] });
    },
  });
}

export function useUpdateSubject() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => classSettingsService.updateSubject(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SUBJECTS_KEY] });
    },
  });
}

export function useDeleteSubject() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: classSettingsService.deleteSubject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SUBJECTS_KEY] });
    },
  });
}

// Custom Field Definitions
export function useClassCustomFields() {
  return useQuery({
    queryKey: [CUSTOM_FIELDS_KEY],
    queryFn: () => classSettingsService.getCustomFields(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateCustomField() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: classSettingsService.createCustomField,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CUSTOM_FIELDS_KEY] });
    },
  });
}

export function useUpdateCustomField() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }) => classSettingsService.updateCustomField(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CUSTOM_FIELDS_KEY] });
    },
  });
}

export function useDeleteCustomField() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: classSettingsService.deleteCustomField,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CUSTOM_FIELDS_KEY] });
    },
  });
}
